import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table2, Loader2, Trash2, FilePlus2, Save, Quote, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

export default function SavedTablesPanel({
  userId, projectId, chapters = [], refreshKey = 0, onInserted, onAssetsChanged,
  tableWord = 'Table',
}) {
  const [tables, setTables] = useState([]);
  const [caption, setCaption] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState({});
  const [lastTarget, setLastTarget] = useState('');
  const targetFor = (id) => targets[id] ?? lastTarget ?? '';
  const pickTarget = (id, value) => {
    setTargets((t) => ({ ...t, [id]: value }));
    setLastTarget(value);
  };

  const load = useCallback(async () => {
    try {
      setTables(await thesisApi.listTables(userId, projectId));
    } catch (e) {
      console.error(e);
    }
  }, [userId, projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleSave = async () => {
    if (!markdown.trim()) { toast.error('Paste the table content first'); return; }
    setSaving(true);
    try {
      const t = await thesisApi.saveTable(userId, projectId, { caption, markdown, source: 'manual' });
      toast.success(`Saved as ${tableWord} ${t.number}`);
      setCaption('');
      setMarkdown('');
      load();
      onAssetsChanged?.();
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleInsert = async (t) => {
    try {
      const res = await thesisApi.insertTable(userId, t.id, targetFor(t.id) || null);
      toast.success(res.mode === 'new' ? 'Inserted into a new "Tables" section' : 'Table inserted into the section');
      onInserted?.(res.chapter_id);
      onAssetsChanged?.();
    } catch (e) {
      toast.error('Insert failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleInsertRef = async (t) => {
    const chapterId = targetFor(t.id);
    if (!chapterId) { toast.error('Pick the section that should get the reference'); return; }
    try {
      await thesisApi.insertTableReference(userId, t.id, chapterId);
      toast.success(`Reference to ${tableWord} ${t.number} added — it renumbers itself`);
      onInserted?.(chapterId);
    } catch (e) {
      toast.error('Insert failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleMove = async (t, direction) => {
    try {
      await thesisApi.moveTable(userId, t.id, direction);
      await load();
      onAssetsChanged?.();
    } catch (e) {
      toast.error('Reorder failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete ${tableWord} ${t.number}? The remaining tables renumber automatically.`)) return;
    try {
      await thesisApi.deleteTable(userId, t.id);
      toast.success('Table deleted');
      load();
      onAssetsChanged?.();
    } catch (e) {
      toast.error('Delete failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Card data-testid="saved-tables-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Table2 className="w-5 h-5 text-[#1d4ed8]" /> Manuscript tables
          <Badge variant="outline" className="text-[10px]">self-renumbering</Badge>
        </CardTitle>
        <CardDescription>
          Saved tables are exported as real Word / PDF tables (no raw pipes). Captions and in-text
          references renumber automatically when you reorder (↑ ↓) or delete a table.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Input value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption, e.g. Baseline characteristics of the study population"
            data-testid="table-caption-input" />
          <Textarea value={markdown} onChange={(e) => setMarkdown(e.target.value)}
            rows={5} className="font-mono text-xs"
            placeholder={'| Variable | Group A | Group B | p |\n| --- | --- | --- | --- |\n| Age | 54 ± 9 | 56 ± 8 | 0.42 |'}
            data-testid="table-markdown-input" />
          <Button onClick={handleSave} disabled={saving}
            className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white" data-testid="table-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              : <><Save className="w-4 h-4 mr-2" />Save table</>}
          </Button>
        </div>

        {tables.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="tables-empty">
            No saved tables yet.
          </p>
        ) : (
          <div className="space-y-3" data-testid="tables-list">
            {tables.map((t, i) => (
              <div key={t.id} className="p-3 rounded-lg border border-border space-y-2 min-w-0"
                data-testid={`table-row-${t.id}`}>
                <div className="text-sm font-medium">
                  {tableWord} {t.number}. <span className="font-normal">{t.caption || <span className="text-muted-foreground italic">no caption</span>}</span>
                </div>
                <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto max-h-32">{t.markdown}</pre>
                <div className="flex flex-wrap items-center gap-2">
                  <select className="h-8 rounded-md border border-input bg-background px-2 text-xs min-w-[140px]"
                    value={targetFor(t.id)}
                    onChange={(e) => pickTarget(t.id, e.target.value)}
                    data-testid={`table-target-${t.id}`}>
                    <option value="">New "Tables" section</option>
                    {chapters.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => handleInsert(t)}
                    data-testid={`table-insert-${t.id}`}>
                    <FilePlus2 className="w-3.5 h-3.5 mr-1.5" /> Insert table
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleInsertRef(t)}
                    title="Add an in-text reference that renumbers itself"
                    data-testid={`table-insert-ref-${t.id}`}>
                    <Quote className="w-3.5 h-3.5 mr-1.5" /> Insert reference
                  </Button>
                  <Button size="sm" variant="ghost" disabled={i === 0}
                    onClick={() => handleMove(t, 'up')} data-testid={`table-up-${t.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={i === tables.length - 1}
                    onClick={() => handleMove(t, 'down')} data-testid={`table-down-${t.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(t)}
                    data-testid={`table-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                  {t.source !== 'manual' && <Badge variant="secondary" className="text-[10px]">{t.source}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
